import { useState, useEffect } from 'react';
import ReactPlayer from 'react-player/vimeo';
import { Typography, Button, Collapse, } from 'antd';
import { PlayCircleOutlined, ArrowRightOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import "./cleerly.scss";
import { CtaVideos } from '../ctaVideos/ctaVideos.component';
import ProductTypeCode from '../../enums/productTypeCode.enum';
import { useContext } from 'react';
import { UserContext } from '../../contexts/user.context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FlowType from '../../enums/flowType.enum';
import useWidth from '../../hooks/useWidth.hook';
import Breakpoint from '../../enums/breakpoint.enum';
import PosthogHelper from '../../helpers/posthog.helper';
import { FacilitySearch } from '../facilitySearch/facilitySearch.component';
import { listFaqs } from '../../services/faq.service';
import { FaqCode } from '../../enums/index.enum';
import Questions from '../questions/questions.component';

const { Paragraph, Text } = Typography;

export const Cleerly = () => {
  const width = useWidth()
  const { currentUser } = useContext(UserContext)
  const [expandedParagraphs, setExpandedParagraphs] = useState({});
  const [videosDrawerVisible, setVideosDrawerVisible] = useState(false);
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [faqs, setFaqs] = useState([])

  useEffect(() => {
    document.title = `Instalab | Cleerly: Coronary CT Angiogram`
    autoOpenVideos()
  }, [])


  useEffect(() => {
    const fetchFaqs = async () => {
      const filter = {
        code: { 
          $in: [
            FaqCode.CCTA_WHAT_IS, 
            FaqCode.CCTA_AGE, 
            FaqCode.CCTA_HOW_OFTEN, 
            FaqCode.CCTA_PRICE_COMPARISON, 
            FaqCode.CCTA_BETTER_TEST, 
            FaqCode.CCTA_LONGEVITY
          ]   
        }
      }
      const response = await listFaqs({filter})
      setFaqs(response)
    }

    fetchFaqs()
  }, [])


  const autoOpenVideos = () => {
    const video = searchParams.get('video')
    if (video === 'true') {
      setVideosDrawerVisible(true)
    }
  }
  
  const showVideosDrawer = () => {
    setVideosDrawerVisible(true);
  };
  
  const closeVideosDrawer = () => {
    setVideosDrawerVisible(false);
  };
  
  // Toggle paragraph expansion
  const toggleParagraph = (id) => {
    setExpandedParagraphs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };


  
  const howContent = [
    {
      id: 'how-1',
      title: `The Scan: Capturing High-Resolution Images`,
      short: "To highlight the plaque in your heart's arteries, a contrast dye is injected into a vein, typically your arm. You may also receive medication to slow your heart rate and improve the clarity of the CT images.",
      full: <>
        To highlight the plaque in your coronary arteries, a contrast dye is injected into a vein, typically your arm. You may also receive medication to slow your heart rate and improve the clarity of the CT images.
        <br/><br/>
        Once ready, you'll lie on a motorized table that slides into the CT scanner. As the machine rotates around you, it captures hundreds of detailed cross-sectional images of your heart within seconds. While the scan itself is quick, expect to spend up to an hour at the imaging center for preparation and post-scan procedures.
      </>,
      img: "/img/ct/procedure.jpg"
    },
    {
      id: 'how-2',
      title: "Image Analysis: Detecting Plaque and Narrowing",
      short: "After your scan, Cleerly's advanced AI software reconstructs a detailed 3D view of your coronary arteries. The software analyzes these images to accurately identify the presence, amount, and type of plaque buildup, as well as areas of artery narrowing.",
      full: <>
        After your scan, Cleerly's advanced AI software reconstructs a detailed 3D view of your coronary arteries. The software analyzes these images to accurately identify the presence, amount, and type of plaque buildup, as well as areas of artery narrowing.
        <br/><br/>
        Cleerly's AI technology is built on data from over 10 million images across more than 30,000 patients collected in multiple clinical trials spanning 15 years. These proprietary algorithms are FDA-cleared and have demonstrated superior accuracy compared to traditional diagnostic methods.
      </>,
      video: `https://vimeo.com/1068358085`

    },
    // Add more paragraphs as needed
  ];

  return (
    <div className="cleerly">
      <CtaVideos
        visible={videosDrawerVisible} 
        onClose={closeVideosDrawer}
        width={900}
        placement="right"
        title={width <= Breakpoint.MD ? "Everything You Need to Know" : "Coronary CTA: Everything You Need to Know"}
      />

      <div className="cleerly-hero">
        <div className="cleerly-section">
          <Paragraph className="cleerly-h1">
            Cleerly: Coronary CT Angiogram
          </Paragraph>
          <Paragraph className="cleerly-p1">
            The most accurate heart health test available. This scan uses high-resolution, AI-enhanced imaging to detect blockages in your heart's arteries before symptoms appear.
          </Paragraph>
          <Button
            type="primary"
            className="schedule-cleerly-btn"
            onClick={() => {
              PosthogHelper.track(currentUser, `[cleerly] clicked schedule cleerly`, true)
              navigate(`/flow/${FlowType.CLEERLY}`)
            }}
          >
            <span className="schedule-cleerly-btn-text">Schedule</span> <ArrowRightOutlined className="schedule-cleerly-btn-icon" />
          </Button>

          <br />

          <Paragraph className="cleerly-quote">
            "Heart disease is the leading killer in the US—but it's treatable, decades before a heart attack. A coronary CTA gives the full picture, detecting both calcified and non-calcified plaque, which can lead to heart attacks or strokes if ruptured. <i>This is life-saving information.</i>"
          </Paragraph>

          <div 
            className="cleerly-hero-img-container"
            onClick={showVideosDrawer}
          >
            <img 
              src="/img/cleerly/hero.jpg" 
              className="cleerly-hero-img" 
            />

            <PlayCircleOutlined className="cleerly-play-icon" />

            <div className="cleerly-video-attribute">
              <Paragraph className="cleerly-video-attribute-name"> Dr. Kimberly Brockenbrough </Paragraph>
              <Paragraph className="cleerly-video-attribute-title"> Cardiac Radiologist </Paragraph>
            </div>
          </div>
        </div>
      </div>
      
      <div className="how-section">
        <div className="cleerly-section">
          <div className="divider-container">
            <Text className="cleerly-h1"> 
              How It Works
            </Text>
          </div>

          {howContent.map((how) => (
            <div key={how.id} className="paragraph-container">
              {how.title && (
                <Paragraph className={`cleerly-p1 bold`}>
                  {how.title}
                </Paragraph>
              )}
              
              <Paragraph className="cleerly-p2">
                {expandedParagraphs[how.id] ? how.full : how.short}
              </Paragraph>
              
              {how.short !== how.full && (
                <Button 
                  type="link" 
                  className="read-more-btn"
                  onClick={() => toggleParagraph(how.id)}
                  icon={expandedParagraphs[how.id] ? <UpOutlined /> : <DownOutlined />}
                >
                  {expandedParagraphs[how.id] ? "Read Less" : "Read More"}
                </Button>
              )}

              <br/>

              {how.img && (
                <img src={how.img} className="cleerly-how-img" />
              )}

              {how.video && (
                <div className="cleerly-how-video-container">
                  <ReactPlayer
                    url={how.video}
                    playing={true}
                    loop={true}
                    muted={true}
                    playsinline={true}
                    controls={false}
                    width="100%"
                    height="auto"
                    className="cleerly-how-video"
                    config={{
                      vimeo: {
                        playerOptions: {
                          responsive: true,
                          background: true
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-section">
        <div className="cleerly-section">
          <div className="divider-container">
            <Text className="cleerly-h1"> 
              Pricing
            </Text>
          </div>
          <Paragraph className="cleerly-p1 pricing-p1">
            We'll help you find the most convenient and affordable testing location near you and handle the appointment scheduling. Enter your location below to search our database.
          </Paragraph>

          <FacilitySearch
            productTypeCodes={[ProductTypeCode.CLEERLY, ProductTypeCode.CONCIERGE_FEE]}
            flowType={FlowType.CLEERLY}
          />
        </div>
      </div>

      {faqs?.length > 0 && (
        <div className="faq-section">
          <div className="cleerly-section">
            <div className="divider-container">
              <Text className="cleerly-h1"> 
              FAQ
            </Text>
          </div>

          <Questions items={faqs} />
          </div>  
        </div>
      )}
    </div>
  );
} 